import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Joyride, { STATUS, EVENTS } from 'react-joyride';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { GraduationCap, Sparkles, ArrowRight, X, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const TutorialContext = createContext(null);

// Tutorial steps organized by page/feature
const tutorialSteps = {
  // Main application tour
  main: [
    {
      target: '[data-testid="nav-logo"]',
      content: 'Welcome to PropertyARV! This guided tour will show you the key features of our real estate investment analysis platform.',
      placement: 'bottom',
      disableBeacon: true,
      title: 'Welcome to PropertyARV!',
    },
    {
      target: '[data-testid="features-dropdown"]',
      content: 'Access all our analysis tools from here: Deal Analyzer, Comp Finder, Project Manager, and more.',
      placement: 'bottom',
      title: 'Features Menu',
    },
    {
      target: '[data-testid="theme-toggle"]',
      content: 'Toggle between light and dark modes for comfortable viewing.',
      placement: 'bottom',
      title: 'Dark Mode',
    },
    {
      target: '[data-testid="help-link"]',
      content: 'Need help? Access our comprehensive help center, FAQs, and video tutorials.',
      placement: 'bottom',
      title: 'Help Center',
    },
  ],
  
  // Deal Analyzer specific tour
  dealAnalyzer: [
    {
      target: '[data-testid="strategy-tabs"]',
      content: 'Choose your investment strategy: Fix & Flip, BRRRR, Rental, Wholesale, New Build, or Commercial.',
      placement: 'bottom',
      disableBeacon: true,
      title: 'Investment Strategy',
    },
    {
      target: 'input[placeholder*="address"]',
      content: 'Enter the property address. Our autocomplete will help you find the exact location.',
      placement: 'bottom',
      title: 'Property Address',
    },
    {
      target: '[data-testid="purchase-price-input"]',
      content: 'Enter the purchase price or asking price of the property.',
      placement: 'right',
      title: 'Purchase Price',
    },
    {
      target: '[data-testid="arv-input"]',
      content: 'Enter the After Repair Value (ARV). Click "Run Comps" to find comparable sales.',
      placement: 'left',
      title: 'After Repair Value',
    },
    {
      target: '[data-testid="repair-cost-input"]',
      content: 'Estimate repair costs or use our Repair Estimator tool for detailed calculations.',
      placement: 'right',
      title: 'Repair Costs',
    },
    {
      target: '[data-testid="financing-section"]',
      content: 'Configure financing options including Hard Money Loans, GAP Funding, and Conventional Loans.',
      placement: 'top',
      title: 'Financing Options',
    },
    {
      target: '[data-testid="results-panel"]',
      content: 'View your analysis results here including profit, ROI, cash-on-cash return, and deal rating.',
      placement: 'left',
      title: 'Analysis Results',
    },
    {
      target: '[data-testid="save-deal-btn"]',
      content: 'Save your analysis to review later or share with partners.',
      placement: 'left',
      title: 'Save Your Deal',
    },
  ],
  
  // Comp Finder tour
  compFinder: [
    {
      target: '[data-testid="comp-search-form"]',
      content: 'Enter a property address to find comparable sales in the area.',
      placement: 'bottom',
      disableBeacon: true,
      title: 'Search for Comps',
    },
    {
      target: '[data-testid="comp-filters"]',
      content: 'Filter results by distance, property type, bedrooms, and more.',
      placement: 'bottom',
      title: 'Filter Results',
    },
    {
      target: '[data-testid="comp-map"]',
      content: 'View comparable properties on an interactive map.',
      placement: 'left',
      title: 'Interactive Map',
    },
    {
      target: '[data-testid="arv-calculator"]',
      content: 'Our ARV Calculator automatically estimates value based on selected comps.',
      placement: 'left',
      title: 'ARV Calculator',
    },
  ],
  
  // CRM tour
  crm: [
    {
      target: '[data-testid="pipeline-tabs"]',
      content: 'Track leads through your sales pipeline: from Lead to Closing.',
      placement: 'bottom',
      disableBeacon: true,
      title: 'Lead Pipeline',
    },
    {
      target: '[data-testid="add-lead-button"]',
      content: 'Add new leads to track seller conversations and property opportunities.',
      placement: 'left',
      title: 'Add New Leads',
    },
    {
      target: '[data-testid="reminders-panel"]',
      content: 'Never miss a follow-up! Your reminders appear here.',
      placement: 'left',
      title: 'Follow-up Reminders',
    },
  ],
  
  // Project Manager tour
  projectManager: [
    {
      target: '[data-testid="project-tabs"]',
      content: 'Manage all aspects of your rehab project: Budget, Scope of Work, Photos, and Reports.',
      placement: 'bottom',
      disableBeacon: true,
      title: 'Project Management',
    },
    {
      target: '[data-testid="budget-tab"]',
      content: 'Track your budget with line items, categories, and draw requests.',
      placement: 'bottom',
      title: 'Budget Tracking',
    },
    {
      target: '[data-testid="scope-tab"]',
      content: 'Create detailed scope of work with room-by-room breakdowns.',
      placement: 'bottom',
      title: 'Scope of Work',
    },
  ],
  
  // Repair Estimator tour
  repairEstimator: [
    {
      target: '[data-testid="estimator-form"]',
      content: 'Enter property details to get an accurate repair cost estimate.',
      placement: 'right',
      disableBeacon: true,
      title: 'Property Details',
    },
    {
      target: '[data-testid="room-selector"]',
      content: 'Select rooms to include in your estimate. Each room has specific repair items.',
      placement: 'bottom',
      title: 'Room Selection',
    },
    {
      target: '[data-testid="repair-items"]',
      content: 'Customize repair items and quantities for each room to get precise costs.',
      placement: 'left',
      title: 'Repair Items',
    },
    {
      target: '[data-testid="estimate-summary"]',
      content: 'View the total estimate breakdown with labor and materials costs.',
      placement: 'top',
      title: 'Estimate Summary',
    },
  ],
  
  // Contracts tour
  contracts: [
    {
      target: '[data-testid="contracts-tabs"]',
      content: 'Browse through different contract categories for real estate transactions.',
      placement: 'bottom',
      disableBeacon: true,
      title: 'Contract Categories',
    },
    {
      target: '[data-testid="contract-preview"]',
      content: 'Preview contracts before downloading. Each template is professionally drafted.',
      placement: 'left',
      title: 'Contract Preview',
    },
    {
      target: '[data-testid="download-contract"]',
      content: 'Download contracts in PDF or Word format. Customize them for your deals.',
      placement: 'bottom',
      title: 'Download Contracts',
    },
    {
      target: '[data-testid="state-addendums"]',
      content: 'Find state-specific addendums that comply with local real estate laws.',
      placement: 'top',
      title: 'State Addendums',
    },
  ],
  
  // Dashboard tour
  dashboard: [
    {
      target: '[data-testid="stats-cards"]',
      content: 'View your portfolio summary at a glance.',
      placement: 'bottom',
      disableBeacon: true,
      title: 'Portfolio Overview',
    },
    {
      target: '[data-testid="recent-deals"]',
      content: 'Quick access to your most recent deal analyses.',
      placement: 'top',
      title: 'Recent Deals',
    },
    {
      target: '[data-testid="quick-actions"]',
      content: 'Jump to any feature with these quick action buttons.',
      placement: 'top',
      title: 'Quick Actions',
    },
  ],
};

// Custom tooltip component for Joyride
const CustomTooltip = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  tooltipProps,
  isLastStep,
  size,
}) => (
  <div
    {...tooltipProps}
    className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md p-0 border border-gray-200 dark:border-gray-700"
  >
    {/* Header */}
    <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-5 py-3 rounded-t-xl">
      <h3 className="font-bold text-lg">{step.title || 'Tutorial'}</h3>
      <p className="text-xs text-purple-100">Step {index + 1} of {size}</p>
    </div>
    
    {/* Content */}
    <div className="p-5">
      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
        {step.content}
      </p>
    </div>
    
    {/* Footer */}
    <div className="flex justify-between items-center px-5 py-3 bg-gray-50 dark:bg-gray-900 rounded-b-xl border-t border-gray-200 dark:border-gray-700">
      <button
        {...closeProps}
        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-sm"
      >
        Skip tour
      </button>
      <div className="flex gap-2">
        {index > 0 && (
          <button
            {...backProps}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Back
          </button>
        )}
        <button
          {...primaryProps}
          className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
        >
          {isLastStep ? 'Finish' : 'Next'}
        </button>
      </div>
    </div>
  </div>
);

export const TutorialProvider = ({ children }) => {
  const { t } = useTranslation();
  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState([]);
  const [currentTour, setCurrentTour] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [hasSeenMainTour, setHasSeenMainTour] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Load tutorial state from localStorage
  useEffect(() => {
    const seen = localStorage.getItem('propertyarv-main-tour-seen');
    setHasSeenMainTour(seen === 'true');
  }, []);

  // Check for new user onboarding trigger
  useEffect(() => {
    const newUserFlag = sessionStorage.getItem('propertyarv-new-user');
    if (newUserFlag === 'true') {
      setIsNewUser(true);
      // Small delay to let the page render first
      const timer = setTimeout(() => {
        setShowWelcomeModal(true);
      }, 1000);
      // Clear the flag so it doesn't show again on refresh
      sessionStorage.removeItem('propertyarv-new-user');
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  // Trigger onboarding for new users (called after registration)
  const triggerOnboarding = useCallback(() => {
    sessionStorage.setItem('propertyarv-new-user', 'true');
    setIsNewUser(true);
  }, []);

  // Start a specific tour
  const startTour = useCallback((tourName) => {
    const tourSteps = tutorialSteps[tourName];
    if (tourSteps && tourSteps.length > 0) {
      setCurrentTour(tourName);
      setSteps(tourSteps);
      setStepIndex(0);
      setRun(true);
    }
  }, []);

  // Stop the current tour
  const stopTour = useCallback(() => {
    setRun(false);
    setCurrentTour(null);
    setStepIndex(0);
  }, []);

  // Mark main tour as seen
  const markMainTourSeen = useCallback(() => {
    localStorage.setItem('propertyarv-main-tour-seen', 'true');
    setHasSeenMainTour(true);
  }, []);

  // Reset all tour progress
  const resetTourProgress = useCallback(() => {
    localStorage.removeItem('propertyarv-main-tour-seen');
    setHasSeenMainTour(false);
  }, []);

  // Get tour for current page
  const getCurrentPageTour = useCallback(() => {
    const path = location.pathname;
    if (path === '/analyzer') return 'dealAnalyzer';
    if (path === '/comps') return 'compFinder';
    if (path === '/crm') return 'crm';
    if (path === '/projects') return 'projectManager';
    if (path === '/dashboard') return 'dashboard';
    if (path === '/repair-estimator') return 'repairEstimator';
    if (path === '/contracts') return 'contracts';
    return null;
  }, [location.pathname]);

  // Handle welcome modal actions
  const handleStartTour = useCallback(() => {
    setShowWelcomeModal(false);
    setIsNewUser(false);
    // Small delay to let modal close
    setTimeout(() => {
      startTour('main');
    }, 300);
  }, [startTour]);

  const handleSkipTour = useCallback(() => {
    setShowWelcomeModal(false);
    setIsNewUser(false);
    markMainTourSeen();
  }, [markMainTourSeen]);

  const handleRemindLater = useCallback(() => {
    setShowWelcomeModal(false);
    setIsNewUser(false);
    // Set a flag to remind on next login
    localStorage.setItem('propertyarv-remind-tour', 'true');
  }, []);

  // Check for remind later flag on component mount
  useEffect(() => {
    const shouldRemind = localStorage.getItem('propertyarv-remind-tour');
    const hasSeenTour = localStorage.getItem('propertyarv-main-tour-seen');
    
    if (shouldRemind === 'true' && hasSeenTour !== 'true') {
      // Show reminder after a short delay on next login
      const timer = setTimeout(() => {
        setShowWelcomeModal(true);
        localStorage.removeItem('propertyarv-remind-tour');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Handle Joyride callbacks
  const handleJoyrideCallback = useCallback((data) => {
    const { status, type, index, action } = data;

    // Update step index for back/forward navigation
    if (type === EVENTS.STEP_AFTER) {
      if (action === 'prev') {
        setStepIndex(index - 1);
      } else {
        setStepIndex(index + 1);
      }
    }

    // Handle tour completion
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRun(false);
      if (currentTour === 'main') {
        markMainTourSeen();
      }
      setCurrentTour(null);
      setStepIndex(0);
    }
  }, [currentTour, markMainTourSeen]);

  const value = {
    run,
    steps,
    stepIndex,
    currentTour,
    hasSeenMainTour,
    startTour,
    stopTour,
    markMainTourSeen,
    resetTourProgress,
    getCurrentPageTour,
    triggerOnboarding,
    availableTours: Object.keys(tutorialSteps),
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
      
      {/* Welcome Modal for New Users */}
      <Dialog open={showWelcomeModal} onOpenChange={setShowWelcomeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <DialogTitle className="text-2xl text-center">{t('tutorialcontext.welcomeToPropertyarv')}</DialogTitle>
            <DialogDescription className="text-center text-base mt-2">
              You're all set! Would you like a quick tour to help you get started?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-purple-50 dark:bg-pink-900/20 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center text-purple-600 dark:text-pink-400 text-sm font-bold">1</div>
                <p className="text-sm text-gray-700 dark:text-gray-300">{t('tutorialcontext.learnAboutOurKeyFeatures')}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center text-purple-600 dark:text-pink-400 text-sm font-bold">2</div>
                <p className="text-sm text-gray-700 dark:text-gray-300">{t('tutorialcontext.discoverNavigationShortcuts')}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center text-purple-600 dark:text-pink-400 text-sm font-bold">3</div>
                <p className="text-sm text-gray-700 dark:text-gray-300">{t('tutorialcontext.findHelpResourcesAnytime')}</p>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={handleRemindLater}
              className="w-full sm:w-auto text-gray-500"
              data-testid="remind-later-btn"
            >
              <Clock className="w-4 h-4 mr-2" />
              Remind me later
            </Button>
            <Button
              variant="outline"
              onClick={handleSkipTour}
              className="w-full sm:w-auto"
            >
              Skip for now
            </Button>
            <Button
              onClick={handleStartTour}
              className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600"
              data-testid="start-tour-btn"
            >
              <GraduationCap className="w-4 h-4 mr-2" />
              Start Tour
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep
        disableOverlayClose
        spotlightClicks
        callback={handleJoyrideCallback}
        tooltipComponent={CustomTooltip}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: '#9333ea',
            arrowColor: '#fff',
          },
          spotlight: {
            borderRadius: 8,
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip',
        }}
      />
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

export default TutorialContext;
